import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Chip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import DropZoneWrapper from "../dropwrapper/DropZoneWrapper";

import axios from "axios";
import TipTapEditor from "./TipTapEditor";

const API = process.env.REACT_APP_API_URL;

export default function ForwardDialogPro({
  open,
  onClose,
  onSend,
  fromEmail,
  email,
  forwardedHtml,
  forwardOriginalAttachments,
}) {
  const editorRef = useRef(null);

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");

  const [selectedSignature, setSelectedSignature] = useState("main");
  const [signatures, setSignatures] = useState({ main: "", reply: "" });

  const [initialHtml, setInitialHtml] = useState("");

  /* 🔥 NUEVO → adjuntos */
  const [attachments, setAttachments] = useState([]);
  const [originalAttachments, setOriginalAttachments] = useState([]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (name) => {
    setAttachments((prev) => prev.filter((f) => f.name !== name));
  };

  function decodeBase64Safe(b64) {
    if (!b64 || typeof b64 !== "string") return null;

    // Gmail usa base64 URL-SAFE
    let base64 = b64.replace(/-/g, "+").replace(/_/g, "/");

    // Padding
    const padding = 4 - (base64.length % 4);
    if (padding !== 4) {
      base64 += "=".repeat(padding);
    }

    try {
      const raw = atob(base64);
      return Uint8Array.from(raw, (c) => c.charCodeAt(0));
    } catch (err) {
      console.error("Base64 decode ERROR:", err);
      return null;
    }
  }

  useEffect(() => {
    if (!open) return;

    setTo("");
    setSubject(email ? `Fwd: ${email.subject}` : "");
    setAttachments([]);

    if (forwardOriginalAttachments && forwardOriginalAttachments.length > 0) {
      const converted = forwardOriginalAttachments
        .map((a) => {
          const arr = decodeBase64Safe(a.dataBase64);
          if (!arr) return null;

          return new File([arr], a.filename || "archivo", {
            type: a.mimeType || "application/octet-stream",
          });
        })
        .filter(Boolean);

      setOriginalAttachments(converted);
    } else {
      setOriginalAttachments([]);
    }
  }, [open, email, forwardOriginalAttachments]);

  /* Cargar firmas */
  useEffect(() => {
    if (!fromEmail) return;

    axios.get(`${API}/signatures/${fromEmail}`).then((res) => {
      const main = res.data.signature_html || "";
      const reply = res.data.reply_signature_html || main;

      setSignatures({ main, reply });
    });
  }, [fromEmail]);

  /* Combinar firma + bloque reenviado */
  useEffect(() => {
    if (!forwardedHtml) return;

    const sig =
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "";

    const html = `${sig}${forwardedHtml}`;

    if (!editorRef.current) {
      setInitialHtml(html);
    } else {
      editorRef.current.commands.setContent(html, false);
    }
  }, [signatures, selectedSignature, forwardedHtml]);

  /* Enviar email reenviado */
  const sendForward = async () => {
    if (!to.trim()) {
      alert("Debe indicar al menos un destinatario.");
      return;
    }

    const normalize = (str) => str.replace(/;/g, ",").replace(/\s+/g, "");

    const html = editorRef.current.getHTML();

    const form = new FormData();
    form.append("fromEmail", fromEmail);
    form.append("to", normalize(to));
    form.append("subject", subject);
    form.append("html", html);

    // 🔥 añadir adjuntos al form
    attachments.forEach((file) => form.append("attachments", file));

    // adjuntos originales
    originalAttachments.forEach((file) => form.append("attachments", file));

    await onSend(form);
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Reenviar mensaje
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* PARA */}
        <TextField
          fullWidth
          size="small"
          label="Para"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ASUNTO */}
        <TextField
          fullWidth
          size="small"
          label="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* SELECT FIRMA */}
        <Select
          fullWidth
          size="small"
          value={selectedSignature}
          onChange={(e) => setSelectedSignature(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="main">Firma principal</MenuItem>
          <MenuItem value="reply">Firma respuesta</MenuItem>
          <MenuItem value="none">Sin firma</MenuItem>
        </Select>

        {/* 🔥 Botón adjuntar */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          component="label"
          sx={{ mb: 1 }}
        >
          Adjuntar archivos
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>
        {/* 🔥 Adjuntos ORIGINALES */}
        {originalAttachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {originalAttachments.map((file) => (
              <Chip
                key={file.name}
                label={`📎 ${file.name}`}
                onDelete={() =>
                  setOriginalAttachments((prev) =>
                    prev.filter((f) => f.name !== file.name)
                  )
                }
                sx={{ fontSize: 12 }}
              />
            ))}
          </Box>
        )}

        {/* 🔥 Mostrar chips */}
        {attachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {attachments.map((file) => (
              <Chip
                key={file.name}
                label={
                  file.type.startsWith("image/")
                    ? `🖼️ ${file.name}`
                    : `📄 ${file.name}`
                }
                onDelete={() => removeAttachment(file.name)}
                sx={{ fontSize: 12 }}
              />
            ))}
          </Box>
        )}

        {/* EDITOR */}
        {initialHtml ? (
          <DropZoneWrapper
            onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
          >
            <TipTapEditor
              editorRef={editorRef}
              initialHtml={initialHtml}
              onChange={() => {}}
            />
          </DropZoneWrapper>
        ) : (
          <Typography sx={{ color: "#777" }}>Cargando…</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={sendForward}>
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
